import { html, LitElement, TemplateResult, unsafeCSS } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles as componentStyles } from '../view/wizardStep.scss';
import { styles } from '../view/styles';
import { Status, WizardItem } from '../types';

@customElement('gz-wizard-step')
export class GzWizardStepWebComponent extends LitElement implements WizardItem {
  @property({ type: String, reflect: true, attribute: false })
  title: string;

  @property({ type: String, reflect: true, attribute: false })
  message: string;

  @property({ type: Number, reflect: true })
  step: number;

  /**
   * The step status can be either DEFAULT or COMPLETE/INCOMPLETE
   */
  @property({ type: String, reflect: true })
  status: Status = Status.DEFAULT;

  /**
   * This property is true when a step is currently in view.
   */
  @property({ type: Boolean, reflect: true })
  isActive = false;

  /**
   * Whether the completion of step is optional. The user can navigate to the next step only if the current
   * incomplete step is optional.
   */
  @property({ type: Boolean, reflect: true })
  isOptional = false;

  /**
   * Whether the step was visited or not, regardless of its completion status
   */
  @property({ type: Boolean, reflect: true })
  isVisited = false;

  /**
   * Whether the step can be clickable or not. A required but inactive step is enabled if the previous step is
   * validated and completed.
   */
  @property({ type: Boolean, reflect: true })
  isEnabled = false;

  constructor() {
    super();
    this.addEventListener('click', this.onStepClicked);
  }

  static get styles() {
    return [...styles, unsafeCSS(`${componentStyles}`)];
  }

  render() {
    return html`
      <div class="step">
        <span class="line" part="line"></span>
        <span class="threshold"> ${this.renderStepValue()} </span>
        <gz-title fontSize="13" class="title">${this.title}</gz-title>
        <gz-text fontSize="12" class="text">${this.message}</gz-text>
      </div>
    `;
  }

  private renderStepValue(): TemplateResult | number {
    switch (this.status) {
      case Status.COMPLETE:
        return html`<gz-icon
          class="wizard-icon success-icon"
          color="primary"
          name="ds-icon-validation_checked"
          size="25"
        ></gz-icon>`;

      case Status.INCOMPLETE:
        return html`<gz-icon
          class="wizard-icon fail-icon"
          color="negative"
          name="ds-icon-exclamation_wizard"
          size="16"
        ></gz-icon>`;

      default:
        return this.step;
    }
  }

  private onStepClicked(e: MouseEvent) {
    if (!this.isEnabled && !this.isOptional) {
      e.stopImmediatePropagation();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-wizard-step': GzWizardStepWebComponent;
  }
}
